import createOrderV4 from '@dehaat/kisan-app-bl/api/createOrderV4';
import payByPaymentGateway, { PaymentGatewayBody } from '@dehaat/kisan-app-bl/api/payByPaymentGateway';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import { Address, FarmerDetails } from '@dehaat/kisan-app-bl/models/AddressV2';
import { OrderDetailsV4, PaymentOptions } from '@dehaat/kisan-app-bl/models/Order';
import { Vendor } from '@dehaat/kisan-app-bl/models/Vendor';
import axios from 'axios';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import animationStyles from 'src/styles/shimmer.module.css';
import Okay from '@/components/modalActions/Okay';
import ChooseAmountDrawer from '@/components/payments/ChooseAmountDrawer';
import PaymentDrawer from '@/components/payments/PaymentDrawer';
import RoundedBottomDrawer from '@/components/RoundedBottomDrawer';
import Spinner, { SPINNER_TYPE } from '@/components/Spinner';
import SubHeading, { VARIANT } from '@/components/SubHeading';
import { ACCESS_TOKEN_KEY, AVAILABLE_COMBO_STOCK, AVAILABLE_STOCK, CLIENT_NAME_COOKIE, CSC_MERCHANT_DATE_TYPE, IS_PREBOOK_FLOW, MY_CART, NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE, ONE_CLICK_APP_SOURCE, ORDER_ID_KEY, OUT_OF_STOCK_COMBOS, OUT_OF_STOCK_PRODUCTS, SHRI_PROGRAM_USER_DETAILS } from '@/constants/common';
import { CSC } from '@/constants/csc';
import { KYC_AADHAR_KEY, KYC_PAN_CARD_KEY } from '@/constants/insurance';
import { CSC_LOGIN } from '@/constants/login';
import { KISAN_CARD_GATEWAY, PAYMENT_OPTIONS } from '@/constants/payments';
import { SPICE_MONEY } from '@/constants/spiceMoney';
import { CartContext } from '@/context/CartProvider';
import { ErrorModalContext } from '@/context/ErrorModalProvider';
import { InsuranceContext } from '@/context/InsuranceProvider';
import { UserContext } from '@/context/UserProvider';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import buttonLoader from '@/images/loader/pageLoader.gif';
import { InsuranceDataItem } from '@/models/api/productInsurance';
import { CartValidationError, OrderStatusDetails, OTPData } from '@/models/Cart';
import { ComboVariant2 } from '@/models/Combo';
import { AvailableCoupon } from '@/models/Coupon';
import { OrderError } from '@/models/Order';
import { CartStateInStorage, ProductVariant } from '@/models/Product';
import { ShriUserInLS } from '@/models/shriProgram';
import { capturePurchaseEvent } from '@/utils/analytics';
import { conversionLabels } from '@/utils/analytics/conversionLabels';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import {
// trackCustomEvent,
trackGAConversionEvent } from '@/utils/analytics/googleAnalytics';
import { isShareChatUtmSession } from '@/utils/analytics/utmParams';
import fetchMasterDocumentTypes from '@/utils/api/fetchMasterDocumentTypes';
import initiateCSCPayment from '@/utils/api/initiateCSCPayment';
import payByKisanCard from '@/utils/api/payByKisanCard';
import payBySpiceMoneyWallet from '@/utils/api/payBySpiceMoneyWallet';
import sharePaymentLinkApi, { createSharePaymentLinkRequest } from '@/utils/api/sendPaymentLink';
import URL from '@/utils/axios/url';
import { createOrderQuery, hasInsurance, logCSCOrderToServer, productDiscount, productPrice, updateCartInLocalStorage } from '@/utils/cart';
import { getExternalMetaData } from '@/utils/external';
import { createUUID, generateDateString, getCookieValue, getLocalStorageKey, isFromAgcApp, isProductionBuild, isSessionStorageAvailable, postMessageToWebView, removeLocalStorageKey, setLocalStorageKey } from '@/utils/helper';
import { formatCurrencyWithDecimal } from '@/utils/helpers/formatCurrency';
import logToServer from '@/utils/logger-client';
import parseJWT from '@/utils/parseToken';
import KycPrompt from '../insurance/KycPrompt';
import HypOrderOTPValidation from './HypOrderOTPValidation';
import SelectDeliveryDateModal from './SelectDeliveryDateModal';
const Otp = dynamic(() => import('../insurance/Otp'), {
  ssr: false,
  loading: () => <Spinner />
});
const Kyc = dynamic(() => import('../insurance/Kyc'), {
  ssr: false,
  loading: () => <Spinner />
});
interface Props {
  appliedCoupon: AvailableCoupon | null;
  bankKycStatus: boolean | null;
  cartList: ProductVariant[];
  idKycStatus: boolean | null;
  increaseZIndex: VoidFunction;
  insuranceData: InsuranceDataItem[];
  kycTodoList: string[];
  resetZIndex: VoidFunction;
  selectedAddress: Address | null;
  shippingCharges: number;
  totalDiscount: number;
  totalInsuranceCost: number;
  totalVariantCost: number;
  updateKycData: VoidFunction;
  validateCart: (message: CartValidationError) => void;
  vendor: Vendor;
  isPayNow: boolean;
  prePayAmount: number;
  paymentOptions?: PaymentOptions;
  cumulative_commission: number;
  creditLimit?: number;
  otpData?: OTPData;
  setCreditPayment: (value: boolean) => void;
  showCreditLimit?: boolean;
  setOrderStatusDetails: (value: OrderStatusDetails) => void;
  isValidating: boolean;
  comboVariants?: ComboVariant2[];
}
const PlaceOrder = ({
  isValidating,
  appliedCoupon,
  bankKycStatus,
  cartList,
  idKycStatus,
  increaseZIndex,
  insuranceData,
  kycTodoList,
  resetZIndex,
  selectedAddress,
  shippingCharges,
  totalDiscount,
  totalInsuranceCost,
  totalVariantCost,
  updateKycData,
  validateCart,
  vendor,
  prePayAmount,
  paymentOptions,
  cumulative_commission,
  creditLimit,
  otpData,
  setCreditPayment,
  showCreditLimit = false,
  setOrderStatusDetails,
  comboVariants
}: Props) => {
  const {
    t
  } = useTranslation('cart');
  const {
    cartType,
    cartId,
    cartItems,
    pickupFarmerDetails,
    resetCart,
    isPickupOrder,
    comboItems
  } = useContext(CartContext);
  const paymentDrawerAmountRef = useRef<number>();
  const disablePlaceOrder = Object.keys({
    ...cartItems,
    ...comboItems
  }).length === 0;
  const {
    insuranceItems,
    resetInsuranceItems
  } = useContext(InsuranceContext);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [showSelectAmountDrawer, setShowSelectAmountDrawer] = useState(false);
  const [showValidateOrder, setShowValidateOrder] = useState(false);
  const [selectedOption, setSelectedOption] = useState<PAYMENT_OPTIONS>(PAYMENT_OPTIONS.COD);
  const [bookingDate, setBookingDate] = useState<string>();
  const isPrebookOrder = getLocalStorageKey<CartStateInStorage, null>(MY_CART, null)?.[cartType]?.isPrebook;
  const {
    push,
    reload,
    locale,
    replace,
    asPath
  } = useRouter();
  const [processing, setProcessing] = useState(false);
  const [promptKYC, setPromptKYC] = useState(false);
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const {
    errorModalState,
    setErrorModalState
  } = useContext(ErrorModalContext);
  const couponPrice = appliedCoupon ? Number(appliedCoupon.discount) : 0;
  const finalCost = totalVariantCost + totalInsuranceCost - totalDiscount + shippingCharges - couponPrice;

  // Present only when the backend deems the buyer eligible (active Kisan Card
  // membership). Carries the mode id + balance for the redemption option.
  const kisanCardOption = (paymentOptions as unknown as {
    kisan_card?: {
      payment_mode_id: number;
      balance: number;
    };
  } | undefined)?.kisan_card;
  // Like lender's credit (showCreditLimit), an eligible Kisan Card buyer must reach the
  // payment-method drawer (where the option lives) rather than being routed to the
  // prepay "select amount" flow, which hardcodes the payment gateway.
  const hasKisanCard = Boolean(kisanCardOption);
  const isInsuranceAdded = hasInsurance(insuranceItems, cartItems);
  const isPrePayOrder = !isNaN(prePayAmount) && prePayAmount > 0;
  const [showKycModule, setShowKycModule] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [[aadharTypeId, panTypeId], setIdType] = useState<(number | null)[]>([null, null]);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const {
    user
  } = useContext(UserContext);
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)), []);
  const fromAgc = useMemo(() => Boolean(isFromAgcApp(getCookieValue(CLIENT_NAME_COOKIE))), []);
  const getCSCUser = () => {
    const accessToken = getCookieValue(ACCESS_TOKEN_KEY) || null;
    if (accessToken) {
      const {
        external_partner_id,
        external_partner_type
      } = parseJWT(accessToken);
      if (external_partner_type === CSC_LOGIN.CSC && external_partner_id) return external_partner_id;
    }
    // return 500100100013
  };
  const farmerDetails: FarmerDetails | undefined = useMemo(() => {
    if (pickupFarmerDetails) {
      return {
        auth_id: pickupFarmerDetails.auth_id || '',
        full_name: pickupFarmerDetails.full_name || '',
        id: pickupFarmerDetails.id,
        phone_number: pickupFarmerDetails.phone_number
      };
    } else {
      return selectedAddress?.farmer_details;
    }
  }, [pickupFarmerDetails, selectedAddress]);
  const triggerShowKycModule = async () => {
    setShowKycModule(true);
    const documentTypes = await fetchMasterDocumentTypes('identity_proof_type', [KYC_AADHAR_KEY, KYC_PAN_CARD_KEY], [1, 5]);
    setIdType(documentTypes);
  };
  const onOrderPlaced = (id: number) => {
    if (typeof window !== 'undefined' && window.scq && isShareChatUtmSession()) {
      window.scq('Purchase', 'pre_defined');
    }
    const combosProducts = comboVariants?.map(variant => {
      return {
        productId: variant.parent_id?.toString(),
        brandId: '',
        itemName: variant?.name,
        variantId: variant.id?.toString(),
        discount: variant.discount_amount,
        price: variant.price,
        quantity: comboItems[variant.id] || 0,
        vendorName: vendor.owner_name,
        type: variant.parent_type
      };
    }) || [];
    capturePurchaseEvent({
      orderId: id,
      value: finalCost,
      coupon: appliedCoupon?.couponCode,
      shipping: shippingCharges,
      products: [...cartList.map(variant => ({
        productId: variant.product?.id,
        brandId: variant.product?.brand?.id,
        itemName: variant.product?.name,
        variantId: variant.id,
        discount: productDiscount(variant),
        price: productPrice(variant),
        quantity: cartItems[variant.id] || 0,
        vendorName: vendor.owner_name
      })), ...combosProducts]
    });
    trackGAConversionEvent(CustomEventTypes.ORDER_CREATED, {
      transaction_id: id
    }, conversionLabels.ORDER_CREATED);
    const eventParam = {
      orderId: id,
      UserCoordinates: user?.location?.location?.coordinates,
      BuyerName: farmerDetails?.full_name,
      BuyerNumber: farmerDetails?.phone_number,
      ProductList: cartProductList,
      CartID: cartId,
      OrderAmount: totalVariantCost,
      OrderGrandTotal: finalCost,
      TotalDiscount: totalDiscount,
      CouponCode: appliedCoupon?.couponCode,
      CouponDiscount: appliedCoupon?.discount,
      vendorId: vendor.id,
      vendorName: vendor.company_name
    };
    trackCustomEvent(CustomEventTypes.FARMER_ORDER_PLACED, JSON.stringify(eventParam));
  };
  const placeCSCOrderHandler = async (id: number, cscUserId: string, session_id: string) => {
    const cscIntiatePaymentDetails = {
      merchantId: process.env.NEXT_PUBLIC_CSC_MERCHANT_ID,
      merchantTxn: id.toString(),
      productId: process.env.NEXT_PUBLIC_CSC_WALLET_PRODUCT_ID_B2C,
      merchantTxnDateTime: generateDateString(CSC_MERCHANT_DATE_TYPE),
      txnAmount: finalCost,
      txnMode: CSC.TRANSACTION_MODE,
      txnType: CSC.TRANSACTION_MODE,
      merchantReceiptNo: createUUID(),
      cscShareAmount: cumulative_commission,
      payToEmail: '',
      returnUrl: `${process.env.NEXT_PUBLIC_DIGIACRE_CSC_SUB_DOMAIN}/${URL.CSC_PROCESS_PAYMENT}`,
      cancelUrl: `${process.env.NEXT_PUBLIC_DIGIACRE_CSC_SUB_DOMAIN}/${URL.CSC_PROCESS_PAYMENT}`,
      cscId: cscUserId,
      currency: CSC.CURRENCY,
      amountParameter: 'NA',
      discount: 0,
      param1: session_id,
      param2: id,
      param3: 'NA',
      param4: 'NA',
      encFor: 'Payment'
    };
    await initiateCSCPayment(cscIntiatePaymentDetails);
  };

  // NOTE (Tech debt): this should be a useCallback since
  // it's used down the line after insurance OTP is successful
  // Since it changes every time, OTP handleSubmit is called infinite times
  // resulting in multiple OTPs being created
  // Adding the useCallback dependencies correctly would require careful analysis of this function

  // Hence, creating the tech debt
  const placeOrderHandler = async (hashCode?: string, skipInsurance = false, deliveryDate?: string, paymentOption?: PAYMENT_OPTIONS, paymentGatewayAmount?: number, skipPayment: boolean = false, onOrderCreated?: (paymentSessionLink: string | undefined, orderId: number, paymentOption?: PAYMENT_OPTIONS) => void) => {
    setProcessing(true);
    setShowPaymentDrawer(false);
    setShowSelectAmountDrawer(false);
    setShowDeliveryDateModal(false);
    setShowOtpScreen(false);
    const selection = paymentOption ?? selectedOption;
    // Credit case needs to be handled here once credit feature is available on Mweb

    const paymentModeId = getCSCUser() ? paymentOptions?.csc_wallet.payment_mode_id : selection === PAYMENT_OPTIONS.PAY_NOW ? paymentOptions?.payment_gateway.payment_mode_id : selection === PAYMENT_OPTIONS.CREDIT ? paymentOptions?.credit.payment_mode_id : selection === PAYMENT_OPTIONS.SPICE_MONEY_3P ? paymentOptions?.spice_money_wallet.payment_mode_id : selection === PAYMENT_OPTIONS.KISAN_CARD ? kisanCardOption?.payment_mode_id : paymentOptions?.cash.payment_mode_id;
    const query = createOrderQuery(isPickupOrder, getLocalStorageKey<ShriUserInLS, null>(SHRI_PROGRAM_USER_DETAILS)?.mobileNumber || farmerDetails?.phone_number || '', cartId, cartList, totalVariantCost, skipInsurance ? 0 : totalInsuranceCost, totalDiscount, shippingCharges, true, cartItems, appliedCoupon, selectedAddress, skipInsurance ? {} : insuranceItems, skipInsurance ? [] : insuranceData, !skipInsurance && idKycStatus || false, !skipInsurance && bankKycStatus || false, hashCode, vendor, deliveryDate || bookingDate, false, cumulative_commission, selection === PAYMENT_OPTIONS.CREDIT, undefined, comboVariants, comboItems);
    query.payment = {
      transactions: [{
        payment_mode: paymentModeId,
        amount: getCSCUser() ? finalCost - cumulative_commission : finalCost
      }]
    };

    // FSS-2545: KYC is not required to buy insurance for now as discussed with Satish.
    // Add `&& idKycStatus` to the following IF condition when KYC is required again.
    if (isInsuranceAdded && !skipInsurance && !hashCode) {
      setShowOtpScreen(true);
      setProcessing(false);
      return;
    }
    try {
      const metaData = getExternalMetaData();
      logToServer('info', {
        text: 'Hyperlocal place order',
        query,
        metaData
      });
      const orderResponse = await createOrderV4(query, metaData);
      const {
        error
      } = orderResponse as OrderError;
      if (error) {
        if (error.code === 'CART_01') {
          validateCart({
            message: error.message
          });
        }
      } else {
        const orderDetails = orderResponse as OrderDetailsV4;
        const {
          id
        } = orderDetails.orders[0];
        const {
          request_id
        } = orderDetails;
        onOrderPlaced(request_id);
        const cscUserId = getCSCUser();
        if (selection === PAYMENT_OPTIONS.PAY_NOW || cscUserId || selection === PAYMENT_OPTIONS.SPICE_MONEY_3P || selection === PAYMENT_OPTIONS.KISAN_CARD) {
          placeOrderPGHandler(orderDetails, selection, skipInsurance, paymentGatewayAmount, cscUserId, skipPayment, onOrderCreated);
        } else {
          if ('ReactNativeWebView' in window) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            postMessageToWebView(window, {
              code: 'productOrdered',
              message: id.toString()
            });
          }
          setOrderStatusDetails({
            showOrderStatus: true,
            orderId: id.toString(),
            paymentOption: selectedOption
          });
          resetCart({}, true, true);
          setLocalStorageKey(IS_PREBOOK_FLOW, false);
          removeLocalStorageKey(SHRI_PROGRAM_USER_DETAILS);
          if (isSessionStorageAvailable(window)) {
            sessionStorage.removeItem(ONE_CLICK_APP_SOURCE);
            sessionStorage.removeItem(AVAILABLE_STOCK);
            sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
            sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
            sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
          }
          setProcessing(false);
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log('error', error);
        const hasResponse = Boolean(error.response);
        const hasRequest = Boolean(error.request);
        const status = error.response?.status;
        const code = error.code;
        const redirectUrl = fromApp && !fromAgc ? '/orders?sh=f' : '/orders';
        const isServerGatewayFailure = hasResponse && Boolean(status && status >= 500);
        const isOnline = typeof window !== 'undefined' && window.navigator.onLine;
        const isNoResponseNetworkFailure = hasRequest && code === 'ERR_NETWORK' && isOnline;
        const isTimeoutFailure = code === 'ECONNABORTED' || status === 504;
        logToServer('info', {
          text: 'Hyperlocal place order axios error',
          query,
          error,
          metaData: {
            status,
            code,
            hasResponse,
            hasRequest,
            isServerGatewayFailure,
            isNoResponseNetworkFailure,
            isTimeoutFailure,
            isOnline
          }
        });
        if (isTimeoutFailure || isServerGatewayFailure || isNoResponseNetworkFailure) {
          if (asPath.includes('/cart')) {
            replace(redirectUrl).then(() => {
              resetCart({});
            });
          } else {
            push(redirectUrl).then(() => {
              resetCart({});
            });
          }
          return;
        }
      }
      logToServer('info', {
        text: 'Hyperlocal place order',
        query,
        error
      });
      setErrorModalState({
        ...errorModalState,
        show: true,
        actionNode: <Okay handleClick={() => reload()} />,
        allowClose: false
      });
      setProcessing(false);
    }
  };
  const placeOrderPGHandler = async (orderResponse: OrderDetailsV4, selection?: PAYMENT_OPTIONS, skipInsurance = false, paymentGatewayAmount?: number, cscUserId?: string, skipPayment = false, orderCreatedCallback?: (paymentSessionLink: string | undefined, orderId: number, paymentOption?: PAYMENT_OPTIONS) => void) => {
    try {
      updateCartInLocalStorage(cartType, undefined, undefined, '',
      // createUUID()
      undefined);
      const orderDetails = orderResponse;
      const request_id = orderDetails.request_id;
      const {
        id,
        buyer_details,
        data: {
          address,
          vendor
        }
      } = orderDetails.orders[0];
      if (selection === PAYMENT_OPTIONS.PAY_NOW || cscUserId || selection === PAYMENT_OPTIONS.SPICE_MONEY_3P || selection === PAYMENT_OPTIONS.KISAN_CARD) {
        let totalAmount = finalCost;
        switch (true) {
          case !!cscUserId:
            totalAmount = finalCost;
            break;
          case !!paymentGatewayAmount:
            totalAmount = paymentGatewayAmount as number;
            break;
          case isPrebookOrder:
            totalAmount = prePayAmount;
            break;
          case skipInsurance:
            totalAmount = finalCost - totalInsuranceCost;
            break;
          default:
            totalAmount = finalCost;
        }
        const contactNumber = address?.contact_number ? address.contact_number.substring(address.contact_number.length - 10) : '';
        const body: PaymentGatewayBody = {
          total_amount: totalAmount,
          entity_id: isProductionBuild ? 5 : 4,
          channel: 'Web',
          first_name: address?.contact_name || buyer_details.name || '',
          mobile: contactNumber || buyer_details.number || '',
          billing_address: address?.full_display_address || vendor.company_address || 'NA',
          entity_metadata: {
            vendor: Number(vendor.id),
            order_creation_request_id: request_id
          },
          gateway: cscUserId ? CSC.CSC_GATWAY : selection === PAYMENT_OPTIONS.SPICE_MONEY_3P ? SPICE_MONEY.SPICE_MONEY_GATWAY : selection === PAYMENT_OPTIONS.KISAN_CARD ? KISAN_CARD_GATEWAY : undefined
        };
        const paymentGatewayResponse = await payByPaymentGateway(body);
        if (paymentGatewayResponse) {
          sessionStorage.setItem(ORDER_ID_KEY, id.toString());
          if (paymentGatewayResponse?.session_id && cscUserId) {
            logCSCOrderToServer({
              cscUserId,
              orderDetails,
              finalCost,
              session_id: paymentGatewayResponse?.session_id,
              orderId: id
            });
            await placeCSCOrderHandler(request_id, cscUserId, paymentGatewayResponse?.session_id);
          } else if (paymentGatewayResponse?.session_id && selection === PAYMENT_OPTIONS.SPICE_MONEY_3P) {
            await payBySpiceMoneyWallet(paymentGatewayResponse.session_id, finalCost, request_id);
            setOrderStatusDetails({
              showOrderStatus: true,
              orderId: id.toString(),
              paymentOption: selection
            });
            resetCart({}, true, true);
            setLocalStorageKey(IS_PREBOOK_FLOW, false);
            removeLocalStorageKey(SHRI_PROGRAM_USER_DETAILS);
            if (isSessionStorageAvailable(window)) {
              sessionStorage.removeItem(ONE_CLICK_APP_SOURCE);
              sessionStorage.removeItem(AVAILABLE_STOCK);
              sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
              sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
              sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
            }
            setProcessing(false);
          } else if (paymentGatewayResponse?.session_id && selection === PAYMENT_OPTIONS.KISAN_CARD) {
            const debited = await payByKisanCard(paymentGatewayResponse.session_id, totalAmount);
            if (debited) {
              setOrderStatusDetails({
                showOrderStatus: true,
                orderId: id.toString(),
                paymentOption: selection
              });
              resetCart({}, true, true);
              setLocalStorageKey(IS_PREBOOK_FLOW, false);
              removeLocalStorageKey(SHRI_PROGRAM_USER_DETAILS);
              if (isSessionStorageAvailable(window)) {
                sessionStorage.removeItem(ONE_CLICK_APP_SOURCE);
                sessionStorage.removeItem(AVAILABLE_STOCK);
                sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
                sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
                sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
              }
            } else {
              // Debit failed (backend auto-compensates the points). Send the buyer to
              // their orders, where the order shows as unpaid.
              push(fromApp && !fromAgc ? '/orders?sh=f' : '/orders');
            }
            setProcessing(false);
          } else {
            if (skipPayment) {
              orderCreatedCallback?.(paymentGatewayResponse?.url, id, selection);
            } else {
              push(paymentGatewayResponse.url).then(() => setProcessing(false));
            }
          }
        }
      }
    } catch {
      setErrorModalState({
        ...errorModalState,
        show: true,
        actionNode: <Okay handleClick={() => reload()} />,
        allowClose: false
      });
      setProcessing(false);
    }
  };
  const cartProductList = cartList.map(item => {
    const productName = item.product?.name || '';
    const attributeName = item.attribute_value?.[0]?.name || '';
    return `${productName} ${attributeName}`.trim();
  });
  const PaymentOptionLabels: Record<PAYMENT_OPTIONS, string> = {
    [PAYMENT_OPTIONS.PAY_NOW]: 'PG',
    [PAYMENT_OPTIONS.COD]: 'COD',
    [PAYMENT_OPTIONS.CREDIT]: 'Credit Line',
    [PAYMENT_OPTIONS.CSC]: 'CSC',
    [PAYMENT_OPTIONS.SPICE_MONEY_3P]: 'Spice Money',
    [PAYMENT_OPTIONS.KISAN_CARD]: 'Kisan Card'
  };
  const handlePaymentSelection = (selectedOption: PAYMENT_OPTIONS) => {
    const eventParam = {
      PaymentMode: PaymentOptionLabels[selectedOption],
      BuyerName: farmerDetails?.full_name,
      BuyerNumber: farmerDetails?.phone_number,
      ProductList: cartProductList,
      CartID: cartId,
      OrderAmount: totalVariantCost,
      OrderGrandTotal: finalCost,
      TotalDiscount: totalDiscount,
      CouponCode: appliedCoupon?.couponCode,
      CouponDiscount: appliedCoupon?.discount
    };
    trackCustomEvent(CustomEventTypes.PAYMENT_MODE_SELECTED, JSON.stringify(eventParam));
    setSelectedOption(selectedOption);
    if (selectedOption === PAYMENT_OPTIONS.PAY_NOW) {
      setShowPaymentDrawer(false);
      setShowSelectAmountDrawer(true);
    } else {
      if (otpData?.required && !isInsuranceAdded) {
        setShowValidateOrder(true);
      } else {
        placeOrderHandler(undefined, undefined, undefined, selectedOption);
      }
    }
  };
  const onPlaceOrderClick = () => {
    const eventParam = {
      UserCoordinates: user?.location?.location?.coordinates,
      BuyerName: farmerDetails?.full_name,
      BuyerNumber: farmerDetails?.phone_number,
      ProductList: cartProductList,
      CartID: cartId,
      OrderAmount: totalVariantCost,
      OrderGrandTotal: finalCost,
      TotalDiscount: totalDiscount,
      CouponCode: appliedCoupon?.couponCode,
      CouponDiscount: appliedCoupon?.discount,
      vendorId: vendor.id,
      vendorName: vendor.company_name
    };
    trackCustomEvent(CustomEventTypes.PLACE_CTA_ORDER_CLICKED, JSON.stringify(eventParam));
    if (isPrebookOrder) {
      setShowDeliveryDateModal(true);
    } else if (!getCSCUser() && isPrePayOrder && !showCreditLimit && !hasKisanCard) {
      setShowSelectAmountDrawer(true);
    } else if (!getCSCUser()) {
      setShowPaymentDrawer(true);
    } else {
      placeOrderHandler();
    }
  };
  const callSharePaymentLinkApi = async (number: string, paymentSessionUrl: string, orderId: number, paymentOption?: PAYMENT_OPTIONS) => {
    try {
      await sharePaymentLinkApi(createSharePaymentLinkRequest(number, locale || DEFAULT_LANG, paymentSessionUrl));
    } catch (error) {
      setErrorModalState({
        ...errorModalState,
        show: true,
        actionNode: <Okay handleClick={() => {}} />,
        allowClose: true
      });
    }
    paymentOption == undefined ? selectedOption : paymentOption;
    setOrderStatusDetails({
      showOrderStatus: true,
      orderId: orderId.toString(),
      paymentOption: paymentOption == undefined ? selectedOption : paymentOption
    });
    resetCart({}, true, true);
  };
  const handlePaymentLinkShare = (amount: number, number: string) => {
    paymentDrawerAmountRef.current = amount;
    setSelectedOption(PAYMENT_OPTIONS.PAY_NOW);
    placeOrderHandler(undefined, undefined, bookingDate, PAYMENT_OPTIONS.PAY_NOW, amount, true, (url, orderId, paymentOption) => {
      url && callSharePaymentLinkApi(number, url, orderId, paymentOption);
    });
  };
  useEffect(() => {
    if (showKycModule || showOtpScreen || showDeliveryDateModal || showValidateOrder || showSelectAmountDrawer) {
      increaseZIndex();
    } else {
      resetZIndex();
    }
  }, [increaseZIndex, resetZIndex, showKycModule, showOtpScreen, showDeliveryDateModal, showValidateOrder, showSelectAmountDrawer]);
  return <>
      {showDeliveryDateModal && <SelectDeliveryDateModal onModalClose={() => setShowDeliveryDateModal(false)} handleContinue={date => {
      setBookingDate(date);
      if (isPrePayOrder && !showCreditLimit && !hasKisanCard && !getCSCUser()) {
        setShowSelectAmountDrawer(true);
      } else if (!getCSCUser()) {
        setShowPaymentDrawer(true);
      } else {
        placeOrderHandler(undefined, undefined, date);
      }
    }} />}
      {processing && <Spinner type={SPINNER_TYPE.IN_SCREEN} />}
      <div className="flex justify-between items-center">
        <>
          <SubHeading variant={VARIANT.BIG} className="font-semibold" data-sentry-element="SubHeading" data-sentry-source-file="PlaceOrder.tsx">
            {isValidating ? <div className={`w-36 h-4 rounded-lg bg-neutral-30 ${animationStyles.shimmer}`}></div> : formatCurrencyWithDecimal(finalCost, 2)}
          </SubHeading>
          <button className={`${disablePlaceOrder ? 'bg-neutral-30' : 'bg-primary-100'} h-12 px-8 py-3 rounded-lg text-center text-white min-w-[10rem]`} onClick={onPlaceOrderClick} data-testid="place-order-btn" disabled={disablePlaceOrder}>
            {isValidating ? <Image src={buttonLoader} alt="loading" className="m-auto w-6" /> : isPrebookOrder ? t('place_prebook_order') : t('place_order')}
          </button>
        </>
      </div>
      {/* FSS-2545: KYC is not required to buy insurance for now as discussed with Satish.
       Remove `false` from the following IF condition when KYC is required again. */}
      {false && isInsuranceAdded && !idKycStatus ? <RoundedBottomDrawer show={promptKYC} onClose={() => setPromptKYC(false)}>
          <KycPrompt onCancel={() => {
        setPromptKYC(false);
        placeOrderHandler(undefined, true);
        resetInsuranceItems();
      }} onProceed={() => {
        setPromptKYC(false);
        triggerShowKycModule();
      }} />
        </RoundedBottomDrawer> : null}
      {isInsuranceAdded && showKycModule ? <section className="top-0 left-0 z-50 fixed bg-neutral-10 w-screen h-screen overflow-hidden">
          <Kyc onBack={() => setShowKycModule(false)} onOkay={status => {
        updateKycData();
        if (kycTodoList.includes(status)) {
          setShowOtpScreen(true);
        }
        setShowKycModule(false);
      }} panTypeId={panTypeId} aadharTypeId={aadharTypeId} />
        </section> : null}
      {isInsuranceAdded && showOtpScreen && idKycStatus != null && bankKycStatus != null ? <section className="top-0 left-0 z-50 fixed bg-neutral-10 w-screen h-screen overflow-hidden">
          <Otp bankKycStatus={bankKycStatus}
      // farmerDetails is guaranteed to be set when OTP screen is shown
      // This is done by assertions made while adding insurance
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      farmer={farmerDetails!} idKycStatus={idKycStatus} insuranceData={insuranceData} onBack={() => setShowOtpScreen(false)} onSkipOtp={() => placeOrderHandler(undefined, true)} onSuccess={hascode => placeOrderHandler(hascode, undefined, undefined, undefined, paymentDrawerAmountRef.current)} totalInsuranceCost={totalInsuranceCost} vendor={vendor} />
        </section> : null}

      <PaymentDrawer show={showPaymentDrawer} onClose={() => setShowPaymentDrawer(false)} onConfirmAndProceed={selectedOption => handlePaymentSelection(selectedOption)} creditLimit={creditLimit} maximumAmount={finalCost} minimumAmount={prePayAmount} setCreditPayment={setCreditPayment} showCreditLimit={showCreditLimit} kisanCard={kisanCardOption ? {
      balanceRupees: kisanCardOption.balance,
      orderTotalRupees: finalCost
    } : undefined} data-sentry-element="PaymentDrawer" data-sentry-source-file="PlaceOrder.tsx" />
      <ChooseAmountDrawer minimumAmount={prePayAmount} maximumAmount={finalCost} show={showSelectAmountDrawer} onClose={() => setShowSelectAmountDrawer(false)} handleProceedClick={amount => {
      paymentDrawerAmountRef.current = amount;
      setSelectedOption(PAYMENT_OPTIONS.PAY_NOW);
      placeOrderHandler(undefined, undefined, bookingDate, PAYMENT_OPTIONS.PAY_NOW, amount);
    }} handlePaymentLinkShare={handlePaymentLinkShare} data-sentry-element="ChooseAmountDrawer" data-sentry-source-file="PlaceOrder.tsx" />
      {showValidateOrder && <HypOrderOTPValidation vendorId={vendor.id} farmerDetails={isPickupOrder ? pickupFarmerDetails : selectedAddress?.farmer_details} otpData={otpData} placeHypOrder={hascode => {
      placeOrderHandler(hascode);
      setShowValidateOrder(false);
      setShowPaymentDrawer(false);
    }} handleClose={() => {
      setShowValidateOrder(false);
      setShowPaymentDrawer(false);
    }} />}
    </>;
};
export default PlaceOrder;